import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { SECTIONS, FIELD_TEMPLATES } from "./adminConfig";
import RecordEditor from "./RecordEditor";
import { useSeo } from "../../components/SEO";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ShieldCheck, AlertTriangle, Eye } from "lucide-react";

function label(v) {
  if (v && typeof v === "object") return v.fr || v.en || JSON.stringify(v);
  return String(v ?? "");
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [active, setActive] = useState("settings");
  useSeo("Tableau de bord");

  return (
    <div className="dashboard min-h-screen bg-[#F1F5F9] text-[#1E293B]" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-black/10 bg-[#1E293B] px-6 py-4 text-white">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-[#2A9D8F]" />
          <span className="font-semibold">Doux Instants · Administration</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden text-sm text-white/70 sm:inline">{user?.email}</span>
          <Button onClick={logout} data-testid="admin-logout" size="sm" className="rounded-full bg-white/10 hover:bg-white/20">
            <LogOut className="mr-2 h-4 w-4" /> Déconnexion
          </Button>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px] gap-6 p-6">
        <aside className="hidden w-56 shrink-0 md:block">
          <nav className="space-y-1" aria-label="Sections">
            {SECTIONS.map((s) => (
              <button key={s.key} onClick={() => setActive(s.key)} data-testid={`admin-tab-${s.key}`}
                className={`block w-full rounded-lg px-3 py-2 text-left text-sm font-medium ${active === s.key ? "bg-[#1E293B] text-white" : "text-[#334155] hover:bg-[#E2E8F0]"}`}>
                {s.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1">
          <div className="mb-4 md:hidden">
            <select value={active} onChange={(e) => setActive(e.target.value)} className="h-11 w-full rounded-lg border border-input bg-white px-3">
              {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <SectionView key={active} section={SECTIONS.find((s) => s.key === active)} />
        </main>
      </div>
    </div>
  );
}

function SectionView({ section }) {
  if (section.special) return <SpecialView section={section} />;
  if (section.singleton) return <SingletonView section={section} />;
  return <CollectionView section={section} />;
}

function Card({ children }) {
  return <div className="rounded-xl border border-black/10 bg-white p-5">{children}</div>;
}

function CollectionView({ section }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/${section.key}`);
    setItems(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [section.key]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    await api.delete(`/admin/${section.key}/${id}`);
    toast.success("Supprimé");
    load();
  };

  const startNew = () => setEditing({ ...JSON.parse(JSON.stringify(FIELD_TEMPLATES[section.key] || {})) });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold">{section.label}</h1>
        <Button onClick={startNew} data-testid="admin-add" className="rounded-full bg-[#2A9D8F] hover:bg-[#21867A]">
          <Plus className="mr-2 h-4 w-4" /> Ajouter
        </Button>
      </div>
      {loading ? <p className="text-[#5C6370]">Chargement…</p> : (
        <div className="grid gap-3">
          {items.map((it) => {
            const title = label(it.name || it.title || it.q || it.author || it.caption || it.room_type);
            const flagged = (section.key === "testimonials" && !it.authorized) ||
              ((section.key === "team" || section.key === "gallery") && it.image && !it.photo_authorized);
            return (
              <Card key={it.id}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{title || "(sans titre)"}</p>
                    <p className="text-xs text-[#5C6370]">
                      {it.published === false ? "Masqué · " : ""}
                      {it.status ? `statut: ${it.status}` : ""}
                    </p>
                    {flagged && (
                      <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-amber-700" data-testid={`flag-${it.id}`}>
                        <AlertTriangle className="h-3.5 w-3.5" /> Autorisation non confirmée — non publié
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`edit-${it.id}`} className="rounded-full">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(it.id)} data-testid={`delete-${it.id}`} className="rounded-full text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
          {items.length === 0 && <p className="text-[#5C6370]">Aucun élément.</p>}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifier" : "Ajouter"} · {section.label}</DialogTitle></DialogHeader>
          {editing && (
            <RecordEditor collection={section.key} record={editing}
              onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); load(); }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SingletonView({ section }) {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/${section.key}`);
    setDoc(data || {});
  }, [section.key]);
  useEffect(() => { load(); }, [load]);

  if (!doc) return <p className="text-[#5C6370]">Chargement…</p>;

  const keys = Object.keys(doc).filter((k) => !["key", "updated_at", "_id"].includes(k));
  const isBiling = (v) => v && typeof v === "object" && ("fr" in v || "en" in v);

  const setField = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...doc }; delete payload.updated_at;
      await api.put(`/admin/${section.key}`, payload);
      const officerMissing = section.key === "settings" && (!payload.privacy_officer_name || !payload.privacy_officer_email);
      toast.success(officerMissing ? "Enregistré — responsable vie privée manquant (bloque le lancement)" : "Enregistré");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const officerMissing = section.key === "settings" && (!doc.privacy_officer_name || !doc.privacy_officer_email);

  return (
    <div>
      <h1 className="mb-4 font-display text-2xl font-semibold">{section.label}</h1>
      {officerMissing && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="officer-warning">
          <AlertTriangle className="h-4 w-4" /> Responsable de la protection des renseignements non renseigné — bloque le lancement (Loi 25).
        </div>
      )}
      <Card>
        <div className="grid gap-4">
          {keys.map((k) => {
            const v = doc[k];
            if (typeof v === "boolean") {
              return (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={v} onChange={(e) => setField(k, e.target.checked)} data-testid={`set-${k}`} />
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>
                </label>
              );
            }
            if (isBiling(v)) {
              return (
                <div key={k} className="space-y-1">
                  <Label className="capitalize">{k.replace(/_/g, " ")}</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Textarea placeholder="FR" value={v.fr || ""} onChange={(e) => setField(k, { ...v, fr: e.target.value })} data-testid={`set-${k}-fr`} />
                    <Textarea placeholder="EN" value={v.en || ""} onChange={(e) => setField(k, { ...v, en: e.target.value })} data-testid={`set-${k}-en`} />
                  </div>
                </div>
              );
            }
            return (
              <div key={k} className="space-y-1">
                <Label className="capitalize">{k.replace(/_/g, " ")}</Label>
                <Input value={v ?? ""} onChange={(e) => setField(k, e.target.value)} data-testid={`set-${k}`} />
              </div>
            );
          })}
        </div>
        <Button onClick={save} disabled={saving} data-testid="singleton-save" className="mt-4 rounded-full bg-[#1E293B] hover:bg-[#0f172a]">
          {saving ? "…" : "Enregistrer"}
        </Button>
      </Card>
    </div>
  );
}

function SpecialView({ section }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const endpoint = { submissions: "/admin/submissions/list", consent: "/admin/consent/list", audit: "/admin/audit" }[section.special];

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(endpoint);
    setRows(data || []);
    setLoading(false);
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { await api.put(`/admin/submissions/${id}`, { status }); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/submissions/${id}`); load(); };

  return (
    <div>
      <h1 className="mb-4 font-display text-2xl font-semibold">{section.label}</h1>
      {loading ? <p>Chargement…</p> : (
        <div className="grid gap-3" data-testid={`special-${section.special}`}>
          {rows.map((r, i) => (
            <Card key={r.id || i}>
              {section.special === "submissions" && (
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium">{r.name} <span className="text-xs font-normal text-[#5C6370]">· {r.kind}</span></p>
                    <span className="rounded-full bg-[#E2E8F0] px-2 py-0.5 text-xs">{r.status}</span>
                  </div>
                  <p className="text-sm text-[#5C6370]">{r.email} · {r.phone}</p>
                  {r.message && <p className="mt-1 text-sm">{r.message}</p>}
                  <p className="mt-1 text-xs text-[#94A3B8]">{r.created_at}</p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatus(r.id, "traité")} data-testid={`sub-done-${r.id}`}>Marquer traité</Button>
                    <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => del(r.id)} data-testid={`sub-del-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
              {section.special === "consent" && (
                <div className="text-sm">
                  <p>Fonctionnels : <strong>{r.choices?.functional ? "Oui" : "Non"}</strong> · v{r.policy_version}</p>
                  <p className="text-xs text-[#94A3B8]">{r.at} · {r.ip}</p>
                </div>
              )}
              {section.special === "audit" && (
                <div className="text-sm">
                  <p><strong>{r.action}</strong> · {r.target}</p>
                  <p className="text-xs text-[#94A3B8]">{r.user_email} · {r.at}</p>
                </div>
              )}
            </Card>
          ))}
          {rows.length === 0 && <p className="text-[#5C6370]">Aucune donnée.</p>}
        </div>
      )}
    </div>
  );
}
